import { getAudioContext } from "./ctx";

export type MicSession = {
  rms: () => number;
  timeDomain: () => Uint8Array;
  stop: () => void;
};

export function isMicSupported(): boolean {
  return typeof navigator !== "undefined" && !!navigator.mediaDevices?.getUserMedia;
}

/** Opens the mic and hands back a live RMS/waveform reader. Never records or
    transmits anything — the stream feeds a local AnalyserNode only. */
export async function requestMic(): Promise<MicSession> {
  const stream = await navigator.mediaDevices.getUserMedia({
    audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false },
  });
  const ctx = getAudioContext();
  const source = ctx.createMediaStreamSource(stream);
  const analyser = ctx.createAnalyser();
  analyser.fftSize = 2048;
  source.connect(analyser);

  const timeData = new Uint8Array(analyser.fftSize);

  function timeDomain(): Uint8Array {
    analyser.getByteTimeDomainData(timeData);
    return timeData;
  }

  function rms(): number {
    timeDomain();
    let sum = 0;
    for (let i = 0; i < timeData.length; i++) {
      const v = (timeData[i]! - 128) / 128;
      sum += v * v;
    }
    return Math.sqrt(sum / timeData.length);
  }

  function stop(): void {
    stream.getTracks().forEach((t) => t.stop());
    source.disconnect();
    analyser.disconnect();
  }

  return { rms, timeDomain, stop };
}

/** Samples ambient noise briefly to set a per-session RMS threshold, rather
    than hard-coding one that's wrong in a loud or silent room. */
export function calibrateNoiseFloor(mic: MicSession, ms = 800): Promise<number> {
  return new Promise((resolve) => {
    let peak = 0;
    const start = performance.now();
    function tick() {
      peak = Math.max(peak, mic.rms());
      if (performance.now() - start >= ms) resolve(Math.max(0.02, peak * 1.6));
      else requestAnimationFrame(tick);
    }
    tick();
  });
}

/** RMS-derivative onset detector with a refractory window — call poll() on
    every animation frame; it reports at most one onset per refractory period. */
export function createOnsetDetector(threshold: number, refractoryMs = 60) {
  let lastRms = 0;
  let lastOnsetAt = 0;
  return function poll(mic: MicSession, now: number): boolean {
    const r = mic.rms();
    const delta = r - lastRms;
    lastRms = r;
    if (delta > threshold * 0.5 && r > threshold && now - lastOnsetAt > refractoryMs) {
      lastOnsetAt = now;
      return true;
    }
    return false;
  };
}

/** Simple voice-activity check for "was the mic picking up speech just now." */
export function isVoiced(mic: MicSession, threshold: number): boolean {
  return mic.rms() > threshold;
}
